const RAW_PLACEHOLDER_PREFIX = `@raw<|`;
const RAW_PLACEHOLDER_POSTFIX = `|>`;

/**
 * Replaces all occurrences of `@raw<|...|>` within the input string with the specified CSS values.
 * If `beautify` is true and the pattern does not match, the corresponding value is set to an empty string.
 *
 * @param {string} input - The input string containing CSS properties.
 * @param {ExtractConfig} [config] - Optional configuration objec
 * @returns {string} The string with all custom CSS values extracted, or with empty replacements if `beautify` is true.
 */
export function extractCssCustomValue(
  input: string,
  config?: {
    /**
     * If true, the pattern is replaced with an empty string if it does not match.
     * If false, the pattern is left as is.
     */
    beautify?: boolean;
  }
): string {
  // If the input string does not contain any placeholders, return it as is
  if (!input.includes(RAW_PLACEHOLDER_PREFIX)) return input;

  // Retrieve the 'beautify' flag from the configuration, defaulting to false if not provided
  const beautify = config?.beautify ?? false;

  // Initialize an array to build the result string in parts
  const resultParts = [];
  let i = 0; // Index for traversing the input string

  while (i < input.length) {
    // Find the start index of the next @raw<| pattern
    const rawStart = input.indexOf(RAW_PLACEHOLDER_PREFIX, i);
    if (rawStart === -1) {
      // If no more patterns are found, append the remaining part of the input string
      resultParts.push(input.slice(i));
      break;
    }

    // Append the substring before the @raw<| pattern to the result
    resultParts.push(input.slice(i, rawStart));

    // Calculate the start index of the placeholder's content
    let rawEnd = rawStart + RAW_PLACEHOLDER_PREFIX.length;
    let isEscaping = false; // Flag to indicate if the current character is being escaped
    let isInString = false; // Flag to indicate if we are currently inside a string

    // Loop to find the end of the current @raw<| placeholder
    while (rawEnd < input.length) {
      const currentChar = input[rawEnd];

      // Toggle isInString when encountering non-escaped string delimiters (' or ")
      if (!isEscaping && (currentChar === '"' || currentChar === "'")) {
        isInString = !isInString; // Start or end string mode
      }

      // Check for the end of the placeholder pattern if not in a string and not escaping
      if (
        !isInString &&
        currentChar === '>' &&
        input[rawEnd - 1] === '|' &&
        !isEscaping
      ) {
        break;
      }

      // Toggle isEscaping for escape characters (\)
      isEscaping = !isEscaping && currentChar === '\\';

      rawEnd++; // Move to the next character
    }

    // Extract the content of the placeholder
    const placeholderContent = input.slice(
      rawStart + RAW_PLACEHOLDER_PREFIX.length,
      rawEnd - 1
    );

    // Handle empty placeholders (e.g., @raw<||>)
    if (placeholderContent === '' && input[rawEnd] === '>') {
      resultParts.push(input.slice(rawStart, rawEnd + 1)); // Append empty placeholder as is
    } else {
      // Handle non-empty placeholders
      const rawValue = beautify
        ? placeholderContent.replace(/\\\|/g, '|')
        : placeholderContent; // Replace escaped | with | if beautify is true
      resultParts.push(rawValue); // Append the processed content
    }

    i = rawEnd + 1; // Move past the end of the current placeholder
  }

  return resultParts.join(''); // Join all parts of the result string and return
}

/**
 * Checks if a string contains the `@raw<|...|>` pattern.
 *
 * @param {string} input - The input string to test for the pattern.
 * @returns {boolean} True if the pattern is found, false otherwise.
 */
export function containsCssCustomValue(input: string): boolean {
  return input.indexOf(RAW_PLACEHOLDER_PREFIX) !== -1;
}

/**
 * Wraps a css value in the `@raw<|...|>` format.
 *
 * @param {string} input - The input string containing valid css value.
 * @returns {string} The css value wrapped in the `@raw<|...|>` format.
 */
export function customValuePlaceholder(rawValue: string): string {
  return `${RAW_PLACEHOLDER_PREFIX}${rawValue}${RAW_PLACEHOLDER_POSTFIX}`;
}
